import { useQuery } from '@tanstack/react-query';
import vietmapgl from '@vietmap/vietmap-gl-js/dist/vietmap-gl';
import '@vietmap/vietmap-gl-js/dist/vietmap-gl.css';
import { uniqBy } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// MUI
import { CircularProgress, Paper } from '@mui/material';

// common
import { AppState } from '@redux/store';
import useIsGuest from 'common/hook/useIsGuest';
import useService from 'common/hook/useService';
import { LogoImage, Marker, MarkerSelected } from 'svg';

// screen
import ControllerButton from 'modules/map/components/ControllerButton';
import { focusHvs } from 'modules/map/helpers/focusHvs';
import {
  ClientModel,
  FilterModel,
  GMAP_CUSTOMER_TYPES,
  HVSInfoModel,
} from 'modules/map/types';
import { APP_MAP_KEY } from 'api/API_App';

const HVSPinComponent = (
  <LogoImage style={{ borderRadius: '12px', height: '52px', aspectRatio: '1/1' }} />
);
const HVS_Pin = ReactDOMServer.renderToStaticMarkup(HVSPinComponent);
const Pin = (isActive: boolean) =>
  isActive
    ? ReactDOMServer.renderToStaticMarkup(<MarkerSelected />)
    : ReactDOMServer.renderToStaticMarkup(<Marker />);
function svgStringToImageSrc(svgString) {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
}

const PIN_ACTIVE_SRC = svgStringToImageSrc(Pin(true));
const PIN_INACTIVE_SRC = svgStringToImageSrc(Pin(false));

/** Quá thời gian này mà bản đồ chưa báo load/idle thì vẫn bỏ lớp loading (xem effect khởi tạo). */
const MAP_READY_TIMEOUT = 15000;

function NewMap() {
  const userInformation = useSelector((state: AppState) => state.auth.userInfo);
  const isClient = userInformation?.userAuthen?.userActualManagingOrgType === 'client';
  const isGuest = useIsGuest();
  const [filter, setFilter] = useState<FilterModel>({
    search: '',
    cityId: '',
    wardId: '',
    customerType: '',
  });
  const [focusData, setFocusData] = useState<ClientModel | undefined>(undefined);

  const [openHVS, setOpenHVS] = useState(false);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const service = useService();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state as
    | {
        reopenFoodTraceability?: string;
        tablePage?: number;
        tableRowsPerPage?: number;
      }
    | null;
  const reopenFoodTraceability = navState?.reopenFoodTraceability;
  const [foodTraceabilityRestore, setFoodTraceabilityRestore] = useState<
    { page?: number; rowsPerPage?: number } | undefined
  >(undefined);

  const { data: dataHVS } = useQuery({
    queryKey: ['hvs-info'],
    queryFn: async () => {
      if (!service) return null;
      const json = await service.KiosController_getHvsInfo();
      return json as HVSInfoModel;
    },
    enabled: !!service,
    retry: 1,
    retryDelay: 1000,
  });

  // Đơn vị người dùng đang thuộc về (với user khách hàng = mã CĐS trường của họ; khi tạo tài
  // khoản, org.id được set = client.operationCode nên 2 giá trị này khớp nhau).
  const managingOrgId = userInformation?.userAuthen?.userActualManagingOrgId;

  const { data } = useQuery({
    queryKey: [filter, 'list-school', isClient ? managingOrgId : '', isGuest ? 'guest' : ''],
    queryFn: async () => {
      if (!service) return [];
      // Khách chỉ được xem HVS Profile (docx 07.08 [84]); BE chặn /external/kios/client bằng
      // GuestAccessMiddleware (403 GUEST_FORBIDDEN) nên KHÔNG gọi để tránh lỗi + snackbar vô ích.
      if (isGuest) return [];
      const json = await service.KiosController_listClients();
      // GMAP chỉ hiển thị khách hàng suất ăn cấp 1, 2 (TH/THCS) — Mầm non chỉ cung cấp thực
      // phẩm, CQNN/KHAC không có thực đơn. Dashboard/Quick Search/FSOL vẫn dùng tất cả KH.
      const gmapOnly = (json ?? []).filter((item) =>
        GMAP_CUSTOMER_TYPES.includes(String(item.type))
      );
      // User khách hàng: panel danh sách trường bị ẩn (ControllerButton) và bản đồ chỉ được
      // hiện HVS + đúng trường của họ → phải bó dữ liệu về 1 đơn vị, nếu không marker của
      // TẤT CẢ trường vẫn vẽ ra. Cũng nhờ vậy `data.length === 1` (ControllerButton) mới
      // đúng như thiết kế để tự mở thẻ thông tin trường đó.
      const scoped =
        isClient && managingOrgId
          ? gmapOnly.filter((item) => item.operationCode === managingOrgId)
          : gmapOnly;
      return scoped.map((item, index) => ({ ...item, index }));
    },
    enabled: !!service,
    retry: 1,
    retryDelay: 1000,
  });

  const [map, setMap] = useState<vietmapgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const markersRef = useRef<Map<string, vietmapgl.Marker>>(new Map());
  const focusCodeRef = useRef<string | undefined>(focusData?.operationCode);
  focusCodeRef.current = focusData?.operationCode;
  /** Chỉ auto-focus trường của user khách hàng 1 lần, tránh giật tâm bản đồ khi họ tự kéo. */
  const autoFocusedRef = useRef(false);

  /**
   * Đặt trường đang xem. Thẻ thông tin HVS (HVSInfo, z-30) và thẻ thông tin trường (SchoolInfoBox,
   * z-20) là hai panel RIÊNG, cùng absolute và cùng chỗ, nên khi panel HVS đang mở thì thẻ trường
   * vẽ ở DƯỚI — người dùng bấm trường mà tưởng như không có gì xảy ra. Chọn trường = ý định xem
   * trường, nên đóng panel HVS.
   *
   * Bọc chính hàm set (thay vì dùng useEffect theo dõi focusData) để bao MỌI đường chọn trường —
   * marker trên bản đồ, danh sách trường, thẻ trường ở ControllerButton — kể cả khi bấm lại ĐÚNG
   * trường đang chọn, trường hợp mà effect theo operationCode bỏ sót (tài khoản khách hàng chỉ có
   * một trường thì luôn rơi vào đây).
   *
   * Chỉ đóng HVS khi truyền THẲNG một giá trị: các chỗ cập nhật bằng hàm (ControllerButton đổi
   * activeKey lúc đóng/mở dialog chức năng, auto-focus của tài khoản khách hàng) không phải hành
   * vi chọn trường nên không được đụng tới panel HVS.
   */
  const selectFocusData: typeof setFocusData = (value) => {
    if (typeof value !== 'function' && value) setOpenHVS(false);
    setFocusData(value);
  };

  useEffect(() => {
    if (!reopenFoodTraceability) return;
    setFocusData({
      operationCode: reopenFoodTraceability,
      activeKey: 'foodTraceability',
    } as ClientModel);
    setFoodTraceabilityRestore({
      page: navState?.tablePage,
      rowsPerPage: navState?.tableRowsPerPage,
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [
    reopenFoodTraceability,
    navState?.tablePage,
    navState?.tableRowsPerPage,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    if (!data) return;
    setFocusData((prev) => {
      if (!prev || prev.name) return prev;
      const target = data.find((c) => c.operationCode === prev.operationCode);
      if (!target) return prev;
      return { ...target, activeKey: prev.activeKey };
    });
  }, [data]);

  const filterData = useMemo(() => {
    const tmp =
      data?.filter((item) => {
        return (
          (filter.search
            ? item.name
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLocaleLowerCase()
                .includes(
                  filter.search
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLocaleLowerCase()
                )
            : true) &&
          (filter.cityId ? item.city === filter.cityId : true) &&
          (filter.wardId ? item.wardName === filter.wardId : true) &&
          (filter.customerType ? item.type === filter.customerType : true)
        );
      }) ?? [];
    return uniqBy(tmp.filter(Boolean), 'operationCode');
  }, [data, filter.cityId, filter.customerType, filter.wardId, filter.search]);

  // vietmap
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const HA_NOI: vietmapgl.LngLatLike = [105.849998, 21.033333];

    const mapInstance = new vietmapgl.Map({
      container: mapContainerRef.current,
      style: `https://maps.vietmap.vn/maps/styles/tm/style.json?apikey=${APP_MAP_KEY}`,
      center: HA_NOI,
      zoom: 9,
      fadeDuration: 600,
    }).on('load', () => {
      // Modify the style to hide default markers (POIs)
      const style = mapInstance.getStyle();
      const hasPoiLabel = style.layers?.some((l) => l.id === 'poi-label');
      if (hasPoiLabel) {
        mapInstance.setLayoutProperty('poi-label', 'visibility', 'none');
      }
      mapInstance.addControl(new vietmapgl.NavigationControl(), 'bottom-right');
      style.layers?.forEach((layer) => {
        // Hide specific layers if needed
        if (layer.id.includes('poiz')) {
          mapInstance.setLayoutProperty(layer.id, 'visibility', 'none');
        }
      });
      // Wait for map to load
      setMap(mapInstance);
      // Tắt lớp loading ngay khi style đã load: 'load' đảm bảo style + lần vẽ đầu đã xong.
      // KHÔNG chỉ dựa vào 'idle' — 'idle' đòi "không còn request/animation nào đang chờ", nên
      // chỉ cần một tile treo là nó không nổ, và overlay (pointerEvents:auto khi chưa ready) phủ
      // trắng + chặn click VĨNH VIỄN dù bản đồ bên dưới đã vẽ đủ. Đã gặp thật ở phiên khách.
      setMapReady(true);
    });
    // Giữ 'idle' làm đường tắt thứ hai cho trường hợp 'load' đã nổ trước khi handler kịp gắn.
    mapInstance.once('idle', () => {
      setMapReady(true);
    });
    // Style/tile của vietmap có lúc kẹt giữa đường (đo được: style.json + sprite trả 200 rồi
    // KHÔNG còn request font/tile nào nữa, dù tile gọi trực tiếp vẫn 200). Khi đó 'load' và
    // 'idle' đều không nổ, và lớp loading (pointerEvents:auto) phủ trắng + chặn click vô thời
    // hạn. Hai chốt dưới đây để UI luôn thoát được trạng thái đó: lỗi thì bỏ lớp phủ ngay, còn
    // không thì sau MAP_READY_TIMEOUT vẫn bỏ — thà hiện bản đồ chưa vẽ xong còn hơn màn trắng.
    mapInstance.on('error', (e) => {
      console.error('[GMAP] vietmap-gl lỗi khi tải bản đồ', e?.error ?? e);
      setMapReady(true);
    });
    const readyTimeout = window.setTimeout(() => setMapReady(true), MAP_READY_TIMEOUT);
    // Clean up on unmount
    return () => {
      window.clearTimeout(readyTimeout);
      mapInstance.remove();
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    const markers = new Map<string, vietmapgl.Marker>();
    filterData.forEach((item) => {
      const isActive = isClient ? true : focusCodeRef.current === item.operationCode;
      const svgImage = new Image();
      svgImage.src = isActive ? PIN_ACTIVE_SRC : PIN_INACTIVE_SRC;

      const marker = new vietmapgl.Marker({ element: svgImage })
        .setLngLat([item.lon, item.lat])
        .addTo(map);
      marker.getElement().style.cursor = 'pointer';
      marker.getElement().onclick = () => {
        selectFocusData(item);
        map.flyTo({
          center: [item.lon, item.lat],
          zoom: 14,
          duration: 1000,
        });
      };
      markers.set(item.operationCode, marker);
    });
    markersRef.current = markers;
    return () => {
      markers.forEach((marker) => marker.remove());
      markersRef.current = new Map();
    };
  }, [filterData, isClient, map]);

  useEffect(() => {
    if (isClient) return;
    const newCode = focusData?.operationCode;
    markersRef.current.forEach((marker, code) => {
      const el = marker.getElement() as HTMLImageElement;
      const wantSrc = code === newCode ? PIN_ACTIVE_SRC : PIN_INACTIVE_SRC;
      if (el.src !== wantSrc) el.src = wantSrc;
    });
  }, [focusData?.operationCode, isClient]);

  useEffect(() => {
    if (!map || !dataHVS) return;
    // User khách hàng: tâm bản đồ phải ở trường của họ (effect auto-focus bên dưới), không
    // kéo về HVS — nếu không cú setCenter này sẽ ghi đè flyTo mỗi khi dataHVS về.
    if (!isClient) map.setCenter([Number(dataHVS.latitude), Number(dataHVS.longitude)]);
    const hvsImage = new Image();
    hvsImage.src = svgStringToImageSrc(HVS_Pin);
    const hvsMarker = new vietmapgl.Marker({ element: hvsImage })
      .setLngLat([Number(dataHVS.latitude), Number(dataHVS.longitude)])
      .addTo(map);
    // Marker HVS phải mở được panel như marker khách hàng ở trên (dòng onclick của filterData);
    // thiếu handler này thì marker trông bấm được mà không phản hồi, chỉ thẻ logo góc phải mới mở.
    const hvsElement = hvsMarker.getElement();
    hvsElement.style.cursor = 'pointer';
    hvsElement.onclick = () => focusHvs(map, dataHVS, setOpenHVS);
    return () => {
      hvsMarker.remove();
    };
  }, [dataHVS, map, isClient]);

  // User khách hàng chỉ gắn với 1 trường: vào bản đồ là Ở LUÔN trạng thái đã chọn trường đó
  // (thẻ thông tin trường + nhóm nút chức năng + map zoom vào trường), không phải bấm marker.
  // Các nút/dialog phụ thuộc focusData (useGetRole(focusData) trong ControllerButton) nên
  // bắt buộc phải set focusData, không chỉ dựa vào data.length === 1.
  useEffect(() => {
    if (!isClient || !map || autoFocusedRef.current) return;
    const own = data?.[0];
    if (!own || own.lon == null || own.lat == null) return;
    autoFocusedRef.current = true;
    setFocusData((prev) => prev ?? own); // giữ nguyên nếu đang mở sẵn 1 dialog (reopen...)
    map.flyTo({ center: [own.lon, own.lat], zoom: 14, duration: 1000 });
  }, [data, isClient, map]);

  return (
    <Paper id="google_map" elevation={10} className="paper-list relative">
      <div id="map" ref={mapContainerRef} className="h-full w-full"></div>
      <div
        className="absolute inset-0 flex items-center justify-center bg-white"
        style={{
          opacity: mapReady ? 0 : 1,
          pointerEvents: mapReady ? 'none' : 'auto',
          transition: 'opacity 400ms ease-out',
          zIndex: 1,
        }}
      >
        <CircularProgress />
      </div>
      {data && (
        <ControllerButton
          isClient={isClient}
          isGuest={isGuest}
          map={map}
          data={data}
          filterData={filterData}
          dataHVS={dataHVS ?? undefined}
          filter={filter}
          setFilter={setFilter}
          focusData={focusData}
          setFocusData={selectFocusData}
          foodTraceabilityRestore={foodTraceabilityRestore}
          openHVS={openHVS}
          setOpenHVS={setOpenHVS}
        />
      )}
    </Paper>
  );
}

export default NewMap;
